import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/navbar/ui/Navbar';
import Footer from '../../../components/footer/ui/Footer';
import WhatsAppButton from '../../../components/whatsapp-button/ui/WhatsAppButton';
import ScrollToTop from '../../../components/scroll-to-top/ui/ScrollToTop';
import SEO from '../../../components/seo/SEO';
import FormDialog from '../../../components/form-dialog/ui/FormDialog';
import { useLanguage } from '../../../../context/LanguageContext';
import { Credentials } from '../../../../assets/features/Credentials';
import { submitRecruitmentForm } from '../../../components/email/service/appointmentFormHandler';
import recruitmentBgImage from '../../../../assets/logo/recruitment/recruitment_1.webp';
import recruitmentImage from '../../../../assets/logo/recruitment/recruitment_2.webp';
import { seoData } from '../../../../data/seoData';
import '../style/recruitment.css';

const RecruitmentPage: React.FC = () => {
    const { t, language } = useLanguage();
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        email: '',
        phone: '',
        currentSituation: '',
        position: '',
        message: ''
    });
    const [cvFile, setCvFile] = useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dialogState, setDialogState] = useState<{
        isOpen: boolean;
        type: 'success' | 'error';
        message?: string;
    }>({
        isOpen: false,
        type: 'success'
    });
    const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
    const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
    const [_recaptchaWidgetId, setRecaptchaWidgetId] = useState<number | null>(null);

    useEffect(() => {
        // Scroll to top on mount
        window.scrollTo(0, 0);

        // Debug: Check if reCAPTCHA script is loaded
        
        const checkRecaptcha = () => {
            if (typeof window.grecaptcha !== 'undefined') {
                setRecaptchaLoaded(true);
                
                window.grecaptcha.ready(() => {
                    
                    // Manually render the reCAPTCHA widget
                    setTimeout(() => {
                        const recaptchaContainer = document.querySelector('.g-recaptcha');
                        if (recaptchaContainer && !recaptchaContainer.querySelector('iframe')) {
                            try {
                                const widgetId = window.grecaptcha.render(recaptchaContainer as HTMLElement, {
                                    'sitekey': Credentials.RECAPTCHA_SITE_KEY,
                                    'theme': 'light',
                                    'size': 'normal'
                                });
                                setRecaptchaWidgetId(widgetId);
                                
                                // Verify rendering
                                setTimeout(() => {
                                    const iframe = recaptchaContainer.querySelector('iframe');
                                    if (iframe) {
                                    } else {
                                    }
                                }, 500);
                            } catch (_) {
                                setRecaptchaError('Failed to render reCAPTCHA widget');
                            }
                        } else if (recaptchaContainer && recaptchaContainer.querySelector('iframe')) {
                        } else {
                        }
                    }, 500);
                });
            } else {
            }
        };

        checkRecaptcha();

        let attempts = 0;
        const maxAttempts = 20;
        const checkInterval = setInterval(() => {
            attempts++;
            if (typeof window.grecaptcha !== 'undefined') {
                setRecaptchaLoaded(true);
                setRecaptchaError(null);
                clearInterval(checkInterval);
            } else if (attempts >= maxAttempts) {
                // reCAPTCHA is consent-gated: while the gate notice is shown
                // the script is intentionally absent — keep polling instead of
                // reporting a loading failure.
                if (!document.querySelector('.recaptcha-consent-note')) {
                    setRecaptchaError('reCAPTCHA failed to load. Please refresh the page.');
                    clearInterval(checkInterval);
                }
            }
        }, 500);

        return () => clearInterval(checkInterval);
    }, []);

    // Additional check after component fully renders
    useEffect(() => {
        const checkWidget = () => {
            const recaptchaContainer = document.querySelector('.g-recaptcha');
            if (recaptchaContainer) {
                const iframe = recaptchaContainer.querySelector('iframe');
                if (!iframe) {
                } else {
                }
            } else {
            }
        };

        // Check immediately
        checkWidget();
        
        // Check after a delay to catch late renders
        const timeout = setTimeout(checkWidget, 2000);
        
        return () => clearTimeout(timeout);
    }, [recaptchaLoaded]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const maxSize = 3 * 1024 * 1024; // 3MB in bytes
            
            if (file.size > maxSize) {
                const message = language === 'pt' 
                    ? 'O ficheiro CV é demasiado grande. Por favor, carregue um ficheiro com menos de 3MB.'
                    : 'CV file is too large. Please upload a file smaller than 3MB.';
                setDialogState({
                    isOpen: true,
                    type: 'error',
                    message
                });
                // Clear the file input
                e.target.value = '';
                setCvFile(null);
                return;
            }
            
            setCvFile(file);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        
        // Validate form
        if (!formData.name || !formData.surname || !formData.email || !formData.phone || !formData.currentSituation || !formData.position) {
            const message = t.RECRUITMENT?.fillRequiredFields || (language === 'pt' 
                ? 'Por favor, preencha todos os campos obrigatórios.'
                : 'Please fill in all required fields.');
            setDialogState({
                isOpen: true,
                type: 'error',
                message
            });
            return;
        }
        
        // Validate reCAPTCHA - REQUIRED
        let recaptchaResponse = '';
        if (typeof window.grecaptcha !== 'undefined') {
            try {
                recaptchaResponse = window.grecaptcha.getResponse();
            } catch (e) {
            }
        }
        
        if (!recaptchaResponse) {
            const recaptchaTextarea = document.querySelector('#g-recaptcha-response') as HTMLTextAreaElement;
            if (recaptchaTextarea) {
                recaptchaResponse = recaptchaTextarea.value || '';
            }
        }
        
        if (!recaptchaResponse || recaptchaResponse === '') {
            const message = language === 'pt' 
                ? 'Por favor, complete o reCAPTCHA.'
                : 'Please complete the reCAPTCHA.';
            setDialogState({
                isOpen: true,
                type: 'error',
                message
            });
            return;
        }
        
        setIsSubmitting(true);

        const submissionPayload = {
                ...formData,
                cvFile: cvFile || undefined,
                form_location: 'pagina_recrutamento' as const
            };

        // Optimistic UI: show success immediately, reset form, fire-and-forget the send
            setDialogState({
                isOpen: true,
                type: 'success'
            });
            // Reset form
            setFormData({
                name: '',
                surname: '',
                email: '',
                phone: '',
                currentSituation: '',
                position: '',
                message: ''
            });
            setCvFile(null);
            // Reset file input
            const fileInput = document.getElementById('cvFile') as HTMLInputElement;
            if (fileInput) fileInput.value = '';
        if (typeof window.grecaptcha !== 'undefined') {
            try { window.grecaptcha.reset(); } catch (e) { /* noop */ }
        }
        setIsSubmitting(false);

        submitRecruitmentForm(submissionPayload, language, recaptchaResponse).catch((err) => {
            console.error('Background mail send failed:', err);
        });
    };

    return (
        <>
            <SEO 
                title={seoData.recruitment.title}
                description={seoData.recruitment.description}
                keywords={seoData.recruitment.keywords}
                canonical={seoData.recruitment.canonical}
            />
        <div className="recruitment-page-wrapper">
            <Navbar />
            
            {/* Page Header */}
            <section className="page-header">
                <div
                    className="page-header__bg"
                    style={{
                        backgroundImage: `url(${recruitmentBgImage})`,
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        height: '380px'
                    }}
                ></div>
                <div className="container">
                    <div className="page-header__inner">
                        <div className="page-header__img-1">
                            <img loading="lazy" 
                                src={recruitmentImage} 
                                alt="Roady Oliveira de Azeméis" 
                                style={{ width: '250px', height: '300px', objectFit: 'contain' }}
                            />
                        </div>
                        <h3>{t.NAVIGATION?.recruitment || 'Recrutamento'}</h3>
                        <div className="thm-breadcrumb__inner">
                            <ul className="thm-breadcrumb list-unstyled">
                                <li><Link to="/">{t.NAVIGATION?.start || 'Início'}</Link></li>
                                <li><span className="fas fa-angle-right"></span></li>
                                <li>{t.NAVIGATION?.recruitment || 'Recrutamento'}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* Contact Page */}
            <section className="contact-page">
                <div className="container">
                    <div className="contact-page__bottom">
                        <div className="contact-page__form-box">
                            <h3 className="comment-one__title">{t.RECRUITMENT?.title || 'Junte-se à Nossa Equipa'}</h3>
                            <p className="comment-one__text">
                                {t.RECRUITMENT?.description || 'Candidate-se a uma posição na Roady Oliveira de Azeméis. Todos os campos assinalados * são obrigatórios.'}
                            </p>
                            <form className="contact-page__form" id="recruitment-form" onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="form-page__input-box">
                                            <label htmlFor="name">{t.RECRUITMENT?.labelName || 'Nome *'}</label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                placeholder={t.RECRUITMENT?.placeholderName || 'Insira o seu nome'}
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            <span className="form-page__input-icon">
                                                <i className="icon-user"></i>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="form-page__input-box">
                                            <label htmlFor="surname">{t.RECRUITMENT?.labelSurname || 'Apelido *'}</label>
                                            <input
                                                type="text"
                                                id="surname"
                                                name="surname"
                                                placeholder={t.RECRUITMENT?.placeholderSurname || 'Insira o seu apelido'}
                                                value={formData.surname}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            <span className="form-page__input-icon">
                                                <i className="icon-user"></i>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="form-page__input-box">
                                            <label htmlFor="email">{t.RECRUITMENT?.labelEmail || 'Endereço de Email *'}</label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                placeholder={t.RECRUITMENT?.placeholderEmail || 'Insira o seu email'}
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            <span className="form-page__input-icon">
                                                <i className="icon-email"></i>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="form-page__input-box">
                                            <label htmlFor="phone">{t.RECRUITMENT?.labelPhone || 'Número de Telefone *'}</label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                placeholder={t.RECRUITMENT?.placeholderPhone || 'Insira o seu número de telefone'}
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            <span className="form-page__input-icon">
                                                <i className="icon-phone-call"></i>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="form-page__input-box">
                                            <label htmlFor="currentSituation">{t.RECRUITMENT?.labelCurrentSituation || 'Situação Atual *'}</label>
                                            <div className="form-page__service-selector">
                                                <select
                                                    id="currentSituation"
                                                    name="currentSituation"
                                                    value={formData.currentSituation}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">{t.RECRUITMENT?.optionChoose || 'Escolher...'}</option>
                                                    <option value="student">{t.RECRUITMENT?.optionStudent || 'Estudante'}</option>
                                                    <option value="unemployed">{t.RECRUITMENT?.optionUnemployed || 'Desempregado'}</option>
                                                    <option value="employed">{t.RECRUITMENT?.optionEmployed || 'Empregado'}</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="form-page__input-box">
                                            <label htmlFor="position">{t.RECRUITMENT?.labelPosition || 'Posição a que se Candidata *'}</label>
                                            <div className="form-page__service-selector">
                                                <select
                                                    id="position"
                                                    name="position"
                                                    value={formData.position}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">{t.RECRUITMENT?.optionChoosePosition || 'Escolha uma posição...'}</option>
                                                    <option value="vendedor-rececionista">{t.RECRUITMENT?.optionVendedorRececionista || 'Vendedor / Rececionista'}</option>
                                                    <option value="orcamentista">{t.RECRUITMENT?.optionOrcamentista || 'Orçamentista'}</option>
                                                    <option value="mecanico">{t.RECRUITMENT?.optionMecanico || 'Mecânico'}</option>
                                                    <option value="eletricista">{t.RECRUITMENT?.optionEletricista || 'Eletricista'}</option>
                                                    <option value="mecatronico">{t.RECRUITMENT?.optionMecatronico || 'Mecatrónico'}</option>
                                                    <option value="aprendiz-mecanico">{t.RECRUITMENT?.optionAprendizMecanico || 'Aprendiz de Mecânico'}</option>
                                                    <option value="contabilista-administrativo">{t.RECRUITMENT?.optionContabilistaAdministrativo || 'Contabilista / Administrativo'}</option>
                                                    <option value="operador-repositor">{t.RECRUITMENT?.optionOperadorRepositor || 'Operador / Repositor'}</option>
                                                </select>
                                                <span className="form-page__input-icon">
                                                    <i className="icon-briefcase"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xl-12 col-lg-12">
                                        <div className="form-page__input-box">
                                            <label htmlFor="message">{t.RECRUITMENT?.labelMessage || 'Mensagem'}</label>
                                            <textarea
                                                id="message"
                                                name="message"
                                                placeholder={t.RECRUITMENT?.placeholderMessage || 'Mensagem adicional (opcional)'}
                                                value={formData.message}
                                                onChange={handleInputChange}
                                            ></textarea>
                                            <span className="form-page__input-icon">
                                                <i className="icon-comment"></i>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-xl-12 col-lg-12">
                                        <div className="form-page__input-box">
                                            <label htmlFor="cvFile">{t.RECRUITMENT?.labelUploadCV || 'Carregar CV'}</label>
                                            <input
                                                type="file"
                                                id="cvFile"
                                                name="cvFile"
                                                accept=".pdf,.doc,.docx"
                                                onChange={handleFileChange}
                                                style={{ padding: 0 }}
                                            />
                                            <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                                                {t.RECRUITMENT?.cvHelpText || 'Upload your CV in PDF, DOC (Word), or DOCX format'} (Max: 3MB)
                                            </small>
                                            {cvFile && (
                                                <div style={{ 
                                                    marginTop: '10px', 
                                                    padding: '10px', 
                                                    backgroundColor: '#f8f9fa', 
                                                    borderRadius: '5px',
                                                    border: '1px solid #dee2e6',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px'
                                                }}>
                                                    <span style={{ fontSize: '24px' }}>📄</span>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: 'bold', color: '#333' }}>{cvFile.name}</div>
                                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                                            {(cvFile.size / 1024).toFixed(2)} KB
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setCvFile(null);
                                                            const fileInput = document.getElementById('cvFile') as HTMLInputElement;
                                                            if (fileInput) fileInput.value = '';
                                                        }}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            color: '#d90202',
                                                            cursor: 'pointer',
                                                            fontSize: '18px',
                                                            padding: '5px 10px'
                                                        }}
                                                        title={language === 'pt' ? 'Remover ficheiro' : 'Remove file'}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-xl-12 col-lg-12">
                                        <div className="appointment-one__checkbox-box">
                                            <div className="appointment-one__checkbox">
                                                <input
                                                    type="checkbox"
                                                    id="privacyPolicyRecruitment"
                                                    name="privacy_policy"
                                                    required
                                                />
                                                <label htmlFor="privacyPolicyRecruitment">
                                                    <span className="appointment-one__checkbox-mark"></span>
                                                    {language === 'pt'
                                                        ? <>Aceito a <a href="/politica-privacidade" className="privacy-policy-link-red" target="_blank" rel="noopener noreferrer">Política de Privacidade</a> e concordo com o tratamento dos meus dados pessoais *</>
                                                        : <>I accept the <a href="/politica-privacidade" className="privacy-policy-link-red" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and agree to the processing of my personal data *</>}
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    {/* reCAPTCHA and Submit Button */}
                                    <div className="col-xl-12 col-lg-12">
                                        <div className="appointment-one__recaptcha-btn-row">
                                            <div className="appointment-one__recaptcha-box">
                                                <div className="g-recaptcha" data-sitekey={Credentials.RECAPTCHA_SITE_KEY}></div>
                                                {/* Debug info */}
                                                {!recaptchaLoaded && !recaptchaError && (
                                                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                                        Loading reCAPTCHA...
                                                    </div>
                                                )}
                                                {recaptchaError && (
                                                    <div style={{ fontSize: '12px', color: '#d90202', marginTop: '5px' }}>
                                                        ⚠️ {recaptchaError}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="appointment-one__btn-box">
                                            <button 
                                                type="submit" 
                                                className="form-page__btn" 
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting 
                                                    ? (t.RECRUITMENT?.pleaseWait || (language === 'pt' ? 'Por favor aguarde...' : 'Please wait...'))
                                                    : (t.RECRUITMENT?.submitApplication || (language === 'pt' ? 'Enviar Candidatura' : 'Submit Application'))
                                                }
                                            </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
            
            <FormDialog
                isOpen={dialogState.isOpen}
                type={dialogState.type}
                message={dialogState.message}
                onClose={() => setDialogState({ ...dialogState, isOpen: false })}
            />
            
            <Footer />
            <WhatsAppButton />
            <ScrollToTop />
        </div>
        </>
    );
};

export default RecruitmentPage;
